// SpotBidding ブロック入札タブの表示行 / 約定結果の型。
//
// design HTML: `design/bidding/spot.html` `#tab-block` panel。
// ブロック入札は連続する複数コマを 1 入札単位として扱い、 全コマ約定 or 全コマ不成立
// (all-or-nothing) で部分約定が発生しない方式。 種別は 通常 (STANDARD) / LINK (親子連結) /
// LOOP (売買組合せ)。
import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import type { LicenseType } from '@ebalance/proto/bfftypes/v1/license_type_pb'
import { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import type {
  BiddingBlockBidGroupType,
  BiddingBlockBidStatus,
} from '@ebalance/proto/bffuser/v1/bidding_block_model_pb'
import type {
  BiddingApprovalState,
  BiddingBidSendErrorCategory,
  BiddingBidSendStatus,
} from '@ebalance/proto/bffuser/v1/bidding_common_model_pb'

import type { BadgeProps } from '@enechain/ecloud-designsystem'

import type { BidIssuerType } from '~/models/bidIssuerType'
import {
  bidIssuerTypeOf,
  isUnknownIssuerLicenseType,
} from '~/models/bidIssuerType'
import type { BidWithdrawState } from '~/pages/SpotBidding/models/bidWithdrawState'

/** ブロック種別。 STANDARD=通常 / LINK-P,C=親子連結 / LOOP-A,B=売買組合せ。 */
export type BlockKind = 'STANDARD' | 'LINK-P' | 'LINK-C' | 'LOOP-A' | 'LOOP-B'

/** 入札一覧テーブル 1 行 (= 1 leg = BiddingBlockBid)。 */
export type BlockBidRow = {
  /** leg (BiddingBlockBid) の ID。 */
  id: string
  /** 承認依頼 (group = BiddingBlockApprovalRequest) の ID。 提出ステータス判定に使う。 */
  groupId: string
  /** グループ種別 (= BLOCK / LINK_BLOCK / LOOP_BLOCK)。 約定結果の kind 算出に使う。 */
  groupType: BiddingBlockBidGroupType
  kind: BlockKind
  /**
   * ブロック名称の i18n key (= `page:biddingSpot.blockBidType.*`)。
   * model 層は t() を持てないので key を運び、 描画側 (取下確認のサマリ) で解決する。
   * 一覧は名称の列を持たない (#7520)。
   */
  nameKey: string
  areaCode: AreaCode
  direction: Side
  /** 対象コマ範囲表示 (= "コマ 1〜48 (00:00〜24:00)")。 */
  komaRangeLabel: string
  /**
   * 数量の代表値 (コマ別 MW)。 コマ昇順で先頭コマの入札量。
   *
   * ブロック入札はコマ毎に異なる入札量を取り得る (AC-BSP-030a) ため、 一覧の「数量」列は
   * 本値ではなく [[slotVolumes]] から導く ([[formatBlockVolumeLabel]])。 単一値が必要な
   * 経路 (= コマ別値が取れない fallback) のみ本値を使う。
   */
  volumeMwh: number
  /** 単価 円/kWh。 */
  priceYenPerKwh: number
  /**
   * 約定軸の生値 (= all-or-nothing のため PENDING (約定待ち) / MATCHED (全約定) /
   * UNMATCHED (未約定) / FAILED (eSLJ 側の JEPX 送信自体の失敗) / EXPIRED (失効) / CANCELLED (取下
   * 成立) / WITHDRAWING (取下処理中) / WITHDRAW_FAILED (取下失敗) のいずれか。
   * 部分約定は発生しない)。 承認軸 (approvalState) / 送信軸 (sendStatus) と
   * 合わせてステータス列の 11 区分導出 ([[deriveBlockBidStatusCategory]],
   * AC-BSP-049) に渡す。
   */
  /** AC-BSP-125: 取り込み元の観測。業務状態とは独立。 */
  needsReview?: boolean
  needsReviewSince?: Date
  targetDate?: Date
  bidStatus: BiddingBlockBidStatus
  /**
   * 受渡契約コード (= 発注主体区分×入札エリア×受渡日から広域マスタを解決した BG
   * コード or 計画提出者コード。 ユーザーの自由入力ではなく読み取り専用の応答値、
   * AC-BSP-028)。 作成・更新時に backend が保存した値で、 保存前の旧い leg は undefined。
   */
  deliveryContractCode?: string
  /** JEPX自由記述欄 (= JEPX へ note として送信される、 AC-BSP-028/030)。 省略表示 + hover で全文。 */
  memoJepx?: string
  /** メモ (= 社内限定・JEPXへは送信しない、 AC-BSP-028/030)。 省略表示 + hover で全文。 */
  memoFree?: string
  /**
   * 入札番号 (= JEPX が発番する入札番号、 JEPX 取引規程 第37条。 AC-BSP-028)。
   * leg (LINK-P/C, LOOP-A/B) ごとに別の番号が付く (enechain/ebalance#10308)。
   * 未送信は undefined (= 空表示)。
   */
  bidNo?: string
  /**
   * 作成者 (eauth member ID) / 作成日時 (AC-BSP-028「作成」列)。 グループ (承認依頼) 単位の
   * 監査情報を全 leg 行に展開する。 eBalance 以外から取り込んだ札は作成者が無い (AC-BSP-112)。
   */
  createdBy?: string
  createdAt: Date
  /**
   * 承認ステータス (= 提出ステータスフィルタの backing)。
   * 提出済 = BiddingApprovalState.SUBMITTED / 未提出 = それ以外。
   */
  approvalState: BiddingApprovalState
  /**
   * 送信軸の状態 (= eSLJ への外部送信ステータス: 送信待ち / 送信中 / 提出済 / 送信失敗)。
   * 承認軸 (approvalState) / 約定軸 (status) と直交する第 3 軸。
   * グループ (承認依頼) 単位の軸なので、 グループを構成する全 leg 行に同じ値を展開する。
   */
  sendStatus?: BiddingBidSendStatus
  /**
   * 送信失敗の理由カテゴリ / 生 detail (= sendStatus が失敗系のときのみ意味を持つ)。
   * sendStatus 同様、 グループ単位の軸なので全 leg 行に同じ値を展開する。
   */
  sendErrorCategory?: BiddingBidSendErrorCategory
  sendErrorDetail?: string
  /**
   * backend が応答時点で「JEPX が受け付けていたかを確かめているグループ」 と返したか
   * (AC-BSP-117)。 グループ単位の軸なので全 leg 行に同じ値を展開する。 真の間は、 入札締切の
   * 後もステータス列を 「送信中」 のまま出す ([[deriveBlockBidStatusCategory]])。
   */
  receiptChecking?: boolean
  /**
   * コマ番号配列 (1-origin, 1〜48)。
   * ポジション chart の coverMw 積算と約定結果子テーブルのコマ列に使う。
   */
  slotCodes: readonly number[]
  /**
   * slotCodes と index 対応する約定量 (コマ別 MW) 配列 (= time_slots.contract_volume_mwh)。
   * ポジション chart の executedMw (約定後カバー量) を実データ化するために使う。
   * 未約定 slot は 0。
   */
  slotExecVolumes: readonly number[]
  /**
   * slotCodes と index 対応する約定価格 (円/kWh) 配列 (= time_slots.contract_price_yen_per_kwh)。
   * 約定価格はコマごとに付く。 未約定・約定結果が未着のコマは undefined。
   */
  slotExecPrices: ReadonlyArray<number | undefined>
  /**
   * slotCodes と index 対応する **入札量** (コマ別 MW) 配列 (= time_slots.volume_mwh)。
   * ブロック入札はコマ毎に異なる入札量を指定できる (AC-BSP-030a) ため、 一覧の数量表示と
   * ポジション chart の入札後カバー量はこの配列を使う (= 代表値の 48 倍にしない)。
   */
  slotVolumes: readonly number[]
  /**
   * LINK / LOOP のペア識別子 (= 行左端のカラーバー + グルーピング用)。
   * STANDARD は undefined。
   */
  pairId?: string
  /** 子行 (= LINK-C / LOOP-B) か。 編集 / 削除は親行にだけ出す。 */
  isChild?: boolean
  /**
   * グループの発注主体 (= どの名義で JEPX に発注するか)。 グループ単位の値を全 leg 行に
   * 展開する。 入札一覧の CSV ダウンロードの `発注主体区分` 列に使う。
   */
  issuerType?: LicenseType
  /**
   * backend が応答時点でグループの取下 (AC-BSP-057) を受け付けると返したか。 グループ単位の
   * 値を全 leg 行に展開する (エリアで絞り込んで片方の leg だけが表示されても変わらない)。
   */
  withdrawable: boolean
  /**
   * backend が応答時点でグループの「下書きに戻す」 (AC-BSP-117 / AC-BSP-129) を受け付けると
   * 返したか (送信中のグループ)。 取下と同じくグループ単位の値を全 leg 行に展開する。
   */
  returnableToDraft: boolean
  /**
   * グループを作ったワークフローの業務 ID (AC-BSP-058)。 グループ単位の値を全 leg 行に展開する。
   * ワークフローなし (ワークフロー非経由・記録を始める前に作ったグループ) は undefined。
   */
  workflowId?: string
  /**
   * グループが入札締切まで提出せず失効になったか (AC-BSP-099)。 フィルタ前のグループの全 leg から
   * 決め、 全 leg 行に展開する (行ごとの `bidStatus` で決めると、 同じグループの行とモーダルが食い違う)。
   */
  groupExpired: boolean
  /**
   * グループの取下の進み具合 (AC-BSP-057b)。 フィルタ前のグループの全 leg から決め、全 leg 行に
   * 展開する。 取下が進んでいないグループは undefined。
   */
  withdrawState?: BidWithdrawState
}

/** グループの発注主体を札の発注主体 ([[BidIssuerType]]) に写す。 未供給 (undefined) はそのまま。 */
export const blockRowBidIssuerType = (
  row: Pick<BlockBidRow, 'issuerType'>,
): BidIssuerType | undefined =>
  row.issuerType === undefined ? undefined : bidIssuerTypeOf(row.issuerType)

/** グループの発注主体区分が空欄の取り込んだ札か (#10084)。 未供給 (undefined) は含めない。 */
export const isUnknownIssuerBlockRow = (
  row: Pick<BlockBidRow, 'issuerType'>,
): boolean =>
  row.issuerType !== undefined && isUnknownIssuerLicenseType(row.issuerType)

/** 約定結果の親行 種別 (= 子の P/C, A/B を畳んだ単位)。 */
export type BlockExecKind = 'STANDARD' | 'LINK' | 'LOOP'

/** 約定結果 子テーブル 1 行 (= Leg × コマ の約定)。 */
export type BlockExecChildRow = {
  /** Leg の役割の i18n key (= `page:biddingSpot.blockExec.role.*`)。 STANDARD は undefined (= 「—」)。 */
  legRoleKey?: string
  /** コマ番号 (1〜48)。 */
  koma: number
  areaCode: AreaCode
  direction: Side
  /** 入札量 (MW)。 */
  bidVolumeMw: number
  /** 約定量 (MW)。 未約定は 0。 */
  execVolumeMw: number
  /** 約定価格 円/kWh。 未約定・未着は undefined (= 「—」)。 */
  execPriceYenPerKwh?: number
}

/** 約定結果 親行 (= 親子 accordion の親)。 */
export type BlockExecResult = {
  id: string
  kind: BlockExecKind
  /** グループの Leg のエリア (重複を除く。 LOOP の A・B が別エリアなら 2 つ)。 */
  areaCodes: AreaCode[]
  /** コマ範囲表示 (= "01〜08" or LINK/LOOP の "09〜16 + 33〜40")。 */
  komaRangeLabel: string
  /** 受渡契約コード (= グループ先頭の Leg の値。 保存前の旧い Leg は undefined)。 */
  deliveryContractCode?: string
  /**
   * 状態バッジ文言の i18n key (= `page:biddingSpot.blockExecStatus.*`)。
   * model 層は t() を持てないので key を運び、 描画側で解決する。
   */
  statusLabelKey: string
  /**
   * 状態バッジの DS semantic status。 全約定 = 望ましい終端 (`positive`) /
   * 未約定 = 良否を問わない終端 (`neutral`)。
   */
  statusBadge: BadgeProps['status']
  /** LINK 未約定時に子テーブル上部へ出す all-or-nothing 説明 alert。 */
  note?: { title: string; desc: string }
  children: BlockExecChildRow[]
}
